import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from '@/components/Toast';
import { apiDelete, apiPost } from '@/lib/api';
import {
  deleteConversation as deleteConversationApi,
  renameConversation as renameConversationApi,
} from '@/services/conversationService';
import { useConversationsStore } from '@/stores';

interface UseConversationActionsOptions {
  onRefresh?: () => Promise<void>;
  onCacheClear?: () => void;
}

export function useConversationActions(
  options: UseConversationActionsOptions = {}
) {
  const { onRefresh, onCacheClear } = options;
  const { t } = useTranslation();

  /**
   * Toggle star status on a conversation
   */
  const toggleStar = useCallback(
    async (conversationId: string, currentStarred: boolean) => {
      try {
        const res = await apiPost(
          `/api/ai/conversations/${conversationId}/star`,
          {
            starred: !currentStarred,
          }
        );
        // 🔥 检查后端响应码
        if (res?.code !== 200) {
          showToast(
            res?.message || t('toast.failedToUpdateStarStatus'),
            'error',
            {
              title: t('common.error'),
            }
          );
          return false;
        }
        showToast(
          currentStarred
            ? t('toast.removedFromStarredSuccess')
            : t('toast.addedToStarredSuccess'),
          'success',
          {
            title: t('common.success'),
          }
        );
        // 🔥 更新全局 conversations store，确保 Chats 页面和项目详情页同步
        const { setConversations } = useConversationsStore.getState();
        setConversations(prev =>
          prev.map(conv =>
            String(conv.id) === String(conversationId) ||
            String(conv.conversationId) === String(conversationId)
              ? { ...conv, starred: !currentStarred }
              : conv
          )
        );
        if (onRefresh) {
          await onRefresh();
        }
        if (onCacheClear) {
          onCacheClear();
        }
        // 🔥 刷新侧边栏 Starred 和 Recent chats 列表
        if (window.refreshSidebarStarred) {
          window.refreshSidebarStarred();
        }
        if (window.refreshSidebarRecent) {
          window.refreshSidebarRecent();
        }
        // 🔥 同步 ChatInput 的 starred 状态
        if (window.setCurrentConversationStarred) {
          window.setCurrentConversationStarred(conversationId, !currentStarred);
        }
        return true;
      } catch (error) {
        showToast(t('toast.failedToUpdateStarStatus'), 'error', {
          title: t('common.error'),
        });
        return false;
      }
    },
    [onRefresh, onCacheClear, t]
  );

  /**
   * Rename a conversation
   * 返回 { success: boolean, code?: number }
   */
  const renameConversation = useCallback(
    async (conversationId: string, newTitle: string) => {
      try {
        const res: any = await renameConversationApi(
          conversationId,
          newTitle.trim()
        );
        if (res?.code === 200) {
          showToast('Conversation renamed successfully', 'success', {
            title: 'Successful',
          });
          // 🔥 更新全局 store，确保所有页面同步
          const { setConversations } = useConversationsStore.getState();
          setConversations(prev =>
            prev.map(conv =>
              conv.id === conversationId ||
              conv.conversationId === conversationId
                ? { ...conv, title: newTitle.trim() }
                : conv
            )
          );
          if (onCacheClear) {
            onCacheClear();
          }
          // 异步刷新，不阻塞返回
          if (onRefresh) {
            onRefresh();
          }
          // 🔥 刷新侧边栏 Starred 和 Recent chats 列表
          if (window.refreshSidebarStarred) {
            window.refreshSidebarStarred();
          }
          if (window.refreshSidebarRecent) {
            window.refreshSidebarRecent();
          }
          // 🔥 同步 ChatsPage 的本地 conversations 状态
          if (window.updateConversationTitleInPages) {
            window.updateConversationTitleInPages(
              conversationId,
              newTitle.trim()
            );
          }
          return { success: true, code: 200 };
        } else {
          showToast(res?.message || 'Failed to rename conversation', 'error', {
            title: 'Error',
          });
          return { success: false, code: res?.code };
        }
      } catch (error: any) {
        showToast(error?.message || 'Failed to rename conversation', 'error', {
          title: 'Error',
        });
        return { success: false };
      }
    },
    [onRefresh, onCacheClear]
  );

  /**
   * Delete a single conversation
   * 注意：不显示 toast，因为 DeleteConversationDialog 会处理
   */
  const deleteConversation = useCallback(
    async (conversationId: string) => {
      const res = await deleteConversationApi(conversationId);
      // 🔥 更新全局 store，确保所有页面同步
      const { setConversations } = useConversationsStore.getState();
      setConversations(prev =>
        prev.filter(
          conv =>
            conv.id !== conversationId && conv.conversationId !== conversationId
        )
      );
      if (onRefresh) {
        await onRefresh();
      }
      if (onCacheClear) {
        onCacheClear();
      }
      // 🔥 刷新侧边栏 Starred 和 Recent chats 列表
      if (window.refreshSidebarStarred) {
        window.refreshSidebarStarred();
      }
      if (window.refreshSidebarRecent) {
        window.refreshSidebarRecent();
      }
      // 🔥 同步 ChatsPage 的本地 conversations 状态
      if (window.removeConversationFromPages) {
        window.removeConversationFromPages(conversationId);
      }
      return res;
    },
    [onRefresh, onCacheClear]
  );

  /**
   * Delete multiple conversations
   */
  const deleteMultipleConversations = useCallback(
    async (conversationIds: string[]) => {
      try {
        await apiDelete('/api/ai/conversations/batch', {
          conversationIds,
          _method: 'DELETE',
        });
        showToast(
          `${conversationIds.length} conversation${conversationIds.length > 1 ? 's' : ''} deleted successfully`,
          'success',
          {
            title: 'Successful',
          }
        );
        // 🔥 更新全局 store，确保所有页面同步
        const { setConversations } = useConversationsStore.getState();
        const idsSet = new Set(conversationIds);
        setConversations(prev =>
          prev.filter(
            conv =>
              !idsSet.has(conv.id) && !idsSet.has(conv.conversationId || '')
          )
        );
        if (onRefresh) {
          await onRefresh();
        }
        if (onCacheClear) {
          onCacheClear();
        }
        // 🔥 刷新侧边栏 Starred 和 Recent Chats 列表
        if (window.refreshSidebarStarred) {
          window.refreshSidebarStarred();
        }
        if (window.refreshSidebarRecent) {
          window.refreshSidebarRecent();
        }
        // 🔥 同步 ChatsPage 的本地 conversations 状态
        if (window.removeConversationFromPages) {
          conversationIds.forEach(id => {
            window.removeConversationFromPages?.(id);
          });
        }
        return true;
      } catch (error) {
        showToast('Failed to delete conversations', 'error', {
          title: 'Error',
        });
        return false;
      }
    },
    [onRefresh, onCacheClear]
  );

  /**
   * Move a SINGLE conversation to a project (or remove from project)
   * 🔥 Uses /ai/conversations/{conversation_id}/move endpoint
   * 🔥 For batch operations, use moveToProject instead
   */
  const moveSingleConversation = useCallback(
    async (conversationId: string, projectId: string | null) => {
      try {
        const response = await apiPost<{
          code: number;
          message?: string;
          data?: any;
        }>(`api/ai/conversations/${conversationId}/move`, {
          projectId,
        });

        // 🔥 检查业务 code 是否为 200
        if (response?.code === 200) {
          // ✅ 成功时显示成功 toast 并返回 true
          showToast(
            `Conversation ${projectId ? 'moved' : 'removed from project'} successfully`,
            'success',
            { title: 'Successful' }
          );

          // Update conversation projectId in store directly
          const { setConversations } = useConversationsStore.getState();
          setConversations(prev =>
            prev.map(conv =>
              conv.id === conversationId ||
              conv.conversationId === conversationId
                ? { ...conv, projectId: projectId }
                : conv
            )
          );

          if (onRefresh) {
            await onRefresh();
          }
          if (onCacheClear) {
            onCacheClear();
          }
          return true;
        }
        // ❌ 失败时返回 false
        showToast(
          response?.message ||
            `Failed to ${projectId ? 'move' : 'remove from project'}`,
          'error',
          { title: 'Error' }
        );
        return false;
      } catch (error) {
        showToast(
          `Failed to ${projectId ? 'move' : 'remove from project'}`,
          'error',
          { title: 'Error' }
        );
        return false;
      }
    },
    [onRefresh, onCacheClear]
  );

  /**
   * Move conversation(s) to a project (BATCH operation)
   * 🔥 Uses /api/ai/conversations/move endpoint for multiple conversations
   */
  const moveToProject = useCallback(
    async (conversationIds: string[], projectId: string | null) => {
      try {
        await apiPost('/api/ai/conversations/move', {
          conversationIds,
          projectId,
        });
        showToast(
          `${conversationIds.length} conversation${conversationIds.length > 1 ? 's' : ''} ${projectId ? 'moved' : 'removed from project'} successfully`,
          'success',
          {
            title: 'Successful',
          }
        );
        // Update conversations projectId in store directly
        // This ensures sidebar shows the conversation in the correct section immediately
        const { setConversations } = useConversationsStore.getState();
        setConversations(prev =>
          prev.map(conv =>
            conversationIds.includes(conv.id) ||
            conversationIds.includes(conv.conversationId)
              ? { ...conv, projectId: projectId }
              : conv
          )
        );
        if (onRefresh) {
          await onRefresh();
        }
        if (onCacheClear) {
          onCacheClear();
        }
        return true;
      } catch (error) {
        showToast(
          `Failed to ${projectId ? 'move' : 'remove from'} ${conversationIds.length > 1 ? 'conversations' : 'conversation'}`,
          'error',
          {
            title: 'Error',
          }
        );
        return false;
      }
    },
    [onRefresh, onCacheClear]
  );

  return {
    toggleStar,
    renameConversation,
    deleteConversation,
    deleteMultipleConversations,
    moveToProject,
    moveSingleConversation, // 🔥 单个对话使用的接口
  };
}
